import { useEffect, useState } from 'react';
import { TRACKS, type Track, type TrackKind } from '../../utils/tracks';
import GradeMascot, { MASCOTS as GRADE_MASCOTS } from './GradeMascot';

/**
 * Three-stage level picker with progressive disclosure.
 *
 * Twelve raw track buttons forced a 12-way decision every time. Here the
 * student first decides where they are in school (יסודי / חטיבה / תיכון),
 * then which grade. Only one stage is open at a time — clicking another
 * stage closes the current one. If a value is already set, the stage that
 * contains it is opened by default so a returning user lands on their own
 * row instead of an empty accordion.
 */

interface Props {
  value: Track | null;
  onChange: (track: Track) => void;
  /** Tighter layout for embedded contexts (Settings). Default: spacious. */
  compact?: boolean;
}

interface StageDef {
  kind: TrackKind;
  name: string;
  icon: string;
  desc: string;
  headingClass: string;
}

const STAGES: StageDef[] = [
  { kind: 'elementary',    name: 'יסודי',         icon: '🎒', desc: 'כיתות א׳–ו׳',               headingClass: 'text-orange-300' },
  { kind: 'middle_school', name: 'חטיבת ביניים',   icon: '🎓', desc: 'כיתות ז׳–ט׳',               headingClass: 'text-cyan-300' },
  { kind: 'bagrut',        name: 'תיכון',          icon: '🏛️', desc: 'מסלולי בגרות (3/4/5 יח״ל)', headingClass: 'text-amber-300' },
];

const STAGE_FOR: Record<Track, TrackKind> = Object.fromEntries(
  TRACKS.map((t) => [t.id, t.kind]),
) as Record<Track, TrackKind>;

export default function TrackPicker({ value, onChange, compact = false }: Props) {
  const [openStage, setOpenStage] = useState<TrackKind | null>(value ? STAGE_FOR[value] : null);

  // Follow external value changes (e.g. Settings "switch account type" or a
  // programmatic reset) — but don't fight the user's own header clicks.
  useEffect(() => {
    if (value) setOpenStage(STAGE_FOR[value]);
  }, [value]);

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      {STAGES.map((stage) => {
        const isOpen = openStage === stage.kind;
        const hasActive = value !== null && STAGE_FOR[value] === stage.kind;
        const activeInfo = hasActive && value ? TRACKS.find((t) => t.id === value) : null;
        const tracks = TRACKS.filter((t) => t.kind === stage.kind);
        const panelId = `track-stage-${stage.kind}`;

        return (
          <div key={stage.kind} className="space-y-2">
            <button
              type="button"
              onClick={() => setOpenStage(isOpen ? null : stage.kind)}
              aria-expanded={isOpen}
              aria-controls={panelId}
              className={`w-full flex items-center gap-3 rounded-2xl border-2 transition-all ${
                compact ? 'p-3' : 'p-4'
              } ${
                hasActive
                  ? 'border-accent bg-accent/10'
                  : isOpen
                    ? 'border-slate-500 bg-navy-800/70'
                    : 'border-slate-700/50 hover:border-slate-600 bg-navy-800/50'
              }`}
            >
              <span className={compact ? 'text-2xl' : 'text-3xl'} aria-hidden="true">
                {stage.icon}
              </span>
              <div className="text-right flex-1 min-w-0">
                <div className={`font-bold ${compact ? 'text-base' : 'text-lg'} ${stage.headingClass}`}>
                  {stage.name}
                </div>
                <div className="text-xs text-slate-400 truncate">
                  {activeInfo ? `נבחר: ${activeInfo.displayHe}` : stage.desc}
                </div>
              </div>
              <span
                className={`text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                aria-hidden="true"
              >
                ▸
              </span>
            </button>

            {isOpen && (
              <div
                id={panelId}
                className={
                  compact
                    ? 'grid grid-cols-3 gap-1.5 pr-2'
                    : stage.kind === 'elementary'
                      ? 'grid grid-cols-2 gap-2 pr-2'
                      : 'space-y-2 pr-2'
                }
              >
                {tracks.map((info) => {
                  const active = value === info.id;
                  const hasMascot = info.id in GRADE_MASCOTS;
                  if (compact) {
                    return (
                      <button
                        key={info.id}
                        type="button"
                        onClick={() => onChange(info.id)}
                        title={info.descHe}
                        className={`py-2 px-2 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 ${
                          active
                            ? 'bg-accent text-white shadow-lg shadow-purple-500/20'
                            : 'bg-navy-800 text-slate-400 hover:text-slate-200 border border-slate-700/50'
                        }`}
                      >
                        {hasMascot && <GradeMascot track={info.id} size="sm" animate={active} />}
                        <span>{info.displayHe}</span>
                      </button>
                    );
                  }
                  return (
                    <button
                      key={info.id}
                      type="button"
                      onClick={() => onChange(info.id)}
                      className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center gap-4 ${
                        active
                          ? 'border-accent bg-accent/10 shadow-lg shadow-purple-500/10'
                          : 'border-slate-700/50 hover:border-slate-600 bg-navy-800/50'
                      }`}
                    >
                      <span className="text-2xl" aria-hidden="true">
                        {hasMascot ? <GradeMascot track={info.id} size="sm" /> : info.icon}
                      </span>
                      <div className="text-right flex-1 min-w-0">
                        <div className="font-bold text-lg text-slate-100">{info.displayHe}</div>
                        <div className="text-xs text-slate-400">{info.descHe}</div>
                      </div>
                      {active && <span className="text-accent-light text-xl" aria-hidden="true">✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
